import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef
} from "react";
import { nanoid } from "nanoid";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Box } from "@chakra-ui/react";
import ScrollBox from "src/atoms/ScrollBox";
import { useControls } from "src/utils/createControls";
import NavigationBar from "src/components/NavigationBar";
import PopoverView from "src/components/PopoverView";
import { Tasks } from "src/modules/scheduler/Tasks";
import {
  getEnabled,
  getNotifyAllDevices,
  getDevicesToNotify,
  getTasks
} from "src/modules/scheduler/duck/selectors";
import {
  changeEnabled,
  changeNotifyAllDevices,
  addDeviceToNotify,
  removeDeviceToNotify
} from "src/modules/scheduler/duck/actions";
import { createTestNotification } from "src/modules/scheduler/createNotification";
import { getPairedDevicesArr } from "src/modules/device/duck/selectors";
import { getDeviceName } from "src/modules/auth/duck/selectors";
import { isFreemium } from "src/modules/auth/duck/selectors";
import { usePurchaseContext } from "src/modules/purchase/PurchaseContext";
import { useMessenger } from "src/modules/messenger/Provider";
import debug from "src/modules/debug-logger";

import { ReactComponent as DeleteIcon } from "src/icons/delete-outlined.svg";
import CannotSaveDialog from "./dialogs/CannotSaveDialog";
import DismissChangesDialog from "./dialogs/DismissChangesDialog";
import DeleteDialog from "./dialogs/DeleteDialog";
import SchedulerCreator from "./components/SchedulerCreator";
import Description from "./components/Description";
import EditSchedule from "./components/EditSchedule";
import Notifications from "./components/Notifications";
import { ReactComponent as DescriptionIcon } from "./icons/description.svg";

const SceneEnum = {
  schedulerCreator: 0,
  description: 1,
  editSchedule: 2,
  notifications: 3
};

function SchedulerSettings({ isOpen, onClose, scheduler }) {
  const messenger = useMessenger();
  const purchase = usePurchaseContext();

  const [sceneController, setSceneConstoller] = useState({
    currentScene: SceneEnum.schedulerCreator,
    prevScene: SceneEnum.schedulerCreator,
    sceneProps: null,
    setScene: (scene) => {
      setSceneConstoller((prevState) => ({
        ...prevState,
        prevScene: prevState.currentScene,
        currentScene: scene
      }));
    },
    setSceneProps: (props) => {
      setSceneConstoller((prevState) => ({
        ...prevState,
        sceneProps: props
      }));
    }
  });

  const { currentScene, prevScene, sceneProps, setScene, setSceneProps } =
    sceneController;
  const cannotSaveDialog = useControls();
  const dismissChangesDialog = useControls();
  const deleteDialog = useControls();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const notifyAllDevices = useSelector(getNotifyAllDevices);
  const devicesToNotify = useSelector(getDevicesToNotify);
  const thisDeviceName = useSelector(getDeviceName);
  const devices = useSelector(getPairedDevicesArr);
  const isFreeVersion = useSelector(isFreemium);
  const isEnabled = useSelector(getEnabled);
  const activeTasks = useSelector(getTasks);

  const [isSchedulerEnabled, setIsEnabled] = useState(isEnabled);
  const didMakeAChange = useRef(false);

  useEffect(() => {
    if (isOpen) {
      setScene(SceneEnum.schedulerCreator);
      setSceneProps(null);
    }
  }, [isOpen, setScene, setSceneProps]);

  const deviceList = useMemo(() => {
    return devices
      ?.filter((device) => {
        const deviceIndex = Number(device.jid.substr(device.jid.length - 1));
        return deviceIndex !== 4 && deviceIndex !== 9;
      })
      .map((device) => ({
        ...device,
        checked: devicesToNotify[device.jid] ? true : false,
        onToggle: () => {
          devicesToNotify[device.jid]
            ? dispatch(removeDeviceToNotify(device))
            : dispatch(addDeviceToNotify(device));
        }
      }));
  }, [devices, devicesToNotify, dispatch]);

  const sendTestNotification = useCallback(() => {
    const notification = createTestNotification(thisDeviceName);
    debug.pushNotification(
      `About to send scheduledMonitoringTest to peers`,
      devicesToNotify
    );
    Object.values(devicesToNotify).forEach((device) => {
      messenger.sendPushNotification(device.jid, notification);
    });
  }, [devicesToNotify, messenger, thisDeviceName]);

  const registerOrUpdateTask = useCallback(
    (task) => {
      const newTask = {
        ...task,
        type: Tasks.scheduled_monitoring
      };

      const canRegister = scheduler.canRegisterTask(newTask);
      if (!canRegister) {
        cannotSaveDialog.open();
        return;
      }

      if (!newTask.id) {
        newTask.id = nanoid();
        scheduler.registerTask(newTask);
      } else {
        scheduler.updateTask(newTask);
      }

      setSceneProps(null);
      setScene(SceneEnum.schedulerCreator);
    },
    [cannotSaveDialog, scheduler, setScene, setSceneProps]
  );

  const unregisterTask = useCallback(
    (task) => {
      if (!task?.id) return;
      scheduler.unregisterTask(task);
    },
    [scheduler]
  );

  const handleClose = useCallback(() => {
    dispatch(changeEnabled(isSchedulerEnabled));
    onClose();
  }, [dispatch, onClose, isSchedulerEnabled]);

  const Scenes = useMemo(() => {
    return {
      [SceneEnum.schedulerCreator]: {
        component: (
          <SchedulerCreator
            t={t}
            scheduler={scheduler}
            isEnabled={isSchedulerEnabled}
            openNotifications={() => setScene(SceneEnum.notifications)}
            openSchedule={(schedule) => {
              if (schedule) setSceneProps(schedule);
              setScene(SceneEnum.editSchedule);
            }}
            activeTasks={activeTasks}
            onToggle={() =>
              isFreeVersion && !isEnabled
                ? purchase.open({ content: "scheduler" })
                : setIsEnabled(!isSchedulerEnabled)
            }
            numberOfDevices={Object.keys(devicesToNotify || {})?.length}
            notifyAllDevices={notifyAllDevices}
          />
        ),
        headingKey: "scheduler.heading"
      },
      [SceneEnum.description]: {
        component: <Description t={t} />,
        headingKey: null
      },
      [SceneEnum.editSchedule]: {
        component: (
          <EditSchedule
            t={t}
            scheduler={scheduler}
            schedule={sceneProps}
            onTaskChange={(isDifferent) =>
              (didMakeAChange.current = isDifferent)
            }
            onSubmit={(task) => {
              registerOrUpdateTask(task);
            }}
          />
        ),
        headingKey: "scheduler.create"
      },
      [SceneEnum.notifications]: {
        component: (
          <Notifications
            t={t}
            deviceList={deviceList}
            isEnabled={notifyAllDevices}
            numberOfNotifiedDevices={Object.keys(devicesToNotify || {})?.length}
            onToggle={() => dispatch(changeNotifyAllDevices(!notifyAllDevices))}
            sendTestNotification={sendTestNotification}
          />
        ),
        headingKey: "notifications.heading"
      }
    };
  }, [
    t,
    scheduler,
    isSchedulerEnabled,
    activeTasks,
    devicesToNotify,
    notifyAllDevices,
    sceneProps,
    deviceList,
    sendTestNotification,
    setScene,
    setSceneProps,
    isFreeVersion,
    isEnabled,
    purchase,
    registerOrUpdateTask,
    dispatch
  ]);

  const isInitialStep = currentScene === SceneEnum.schedulerCreator;
  const isInCreation = currentScene === SceneEnum.editSchedule;
  const isInDescription = currentScene === SceneEnum.description;
  const isLastTask = activeTasks.length === 1;
  return (
    <>
      <CannotSaveDialog
        isOpen={cannotSaveDialog.isOpen}
        onDismiss={cannotSaveDialog.close}
      />
      <DismissChangesDialog
        isOpen={dismissChangesDialog.isOpen}
        onDismiss={dismissChangesDialog.close}
        onOk={() => {
          setScene(prevScene);
          setSceneProps(null);
          dismissChangesDialog.close();
        }}
      />
      <DeleteDialog
        isOpen={deleteDialog.isOpen}
        onDismiss={deleteDialog.close}
        onDelete={() => {
          unregisterTask(sceneProps);
          setSceneProps(null);
          setScene(SceneEnum.schedulerCreator);
          deleteDialog.close();
        }}
      />
      <PopoverView isOpen={isOpen}>
        <NavigationBar
          {...(isInDescription && {
            position: "absolute",
            borderBottom: "none"
          })}
          headingKey={Scenes[currentScene].headingKey}
          onBack={
            !isInitialStep
              ? () => {
                  if (isInCreation && sceneProps && didMakeAChange.current) {
                    dismissChangesDialog.open();
                    return;
                  }
                  didMakeAChange.current = false;
                  setScene(prevScene);
                  setSceneProps(null);
                }
              : null
          }
          onClose={isInitialStep && handleClose}
          actionIcon={
            isInCreation && sceneProps && !isLastTask
              ? {
                  icon: DeleteIcon,
                  onClick: () => deleteDialog.open()
                }
              : isInitialStep && {
                  icon: DescriptionIcon,
                  onClick: () => setScene(SceneEnum.description)
                }
          }
          actionButton={
            isInCreation && {
              labelKey: "buttons.save",
              form: "scheduler.creator",
              type: "submit"
            }
          }
        />
        <ScrollBox
          height={isInDescription ? "100%" : "calc(100% - 61px)"}
          overflowX="hidden"
        >
          <Box
            p="24px"
            color="mediumEmphasis"
            sx={{
              h2: {
                color: "highEmphasis"
              }
            }}
          >
            {Scenes[currentScene].component}
          </Box>
        </ScrollBox>
      </PopoverView>
    </>
  );
}

export default React.memo(SchedulerSettings);
